import React from "react";

import { Button, IconButton, Theme } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { makeStyles } from "@material-ui/core/styles";
import { useState } from "react";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import Grid from "@material-ui/core/Grid";
import { useHistory } from "react-router-dom";
import ResetCodeComponent from "../../shared/ResetCodeComponent";

import { loginWithOTP, loginWithSMSOTP } from "../../../store/modules/authSlice";

import { ReactComponent as SMALogo } from "../../../assets/svgs/smalogo.svg";

import { amountActiveSessionExceeded } from "../../../common/phrases";
import { ILoginState } from "../types/authTypes";

interface LoginOTPForTabletsProps {
  finishLoginOTP(isSuccess: boolean, token: string): void;
  username: string;
  password: string;
  isSMS: boolean;
  dispatch: ThunkDispatch<any, any, Action>;
  initialLoginState: ILoginState;
  setLoginState: any;
}

const useStyles = makeStyles((theme: Theme) => ({
  inputs: {
    fontSize: 14,
  },
  button: {
    marginTop: 6,
    marginLeft: 30,
    minWidth: 138,
    backgroundColor: "#FFB500",
    color: "#013270",
    borderRadius: 30,
    "&:hover": {
      color: "#0049a3",
      backgroundColor: "#013270",
    },
    "@media (max-width: 800px)": {
      marginTop: 30,
      marginLeft: 0,
      maxWidth: "170px",
    },
  },
  backButtonContainer: {
    marginTop: 12,
  },
  backButton: {
    minWidth: 54,
    backgroundColor: "rgba(255,255,255, 0.25)",
    color: "white",
    borderRadius: 30,
    "&:hover": {
      color: "white",
      backgroundColor: "rgba(255,255,255, 0.25)",
    },
    padding: "6px 12px",
  },
  alertErrorRoot: {
    marginBottom: 20,
  },
  textFieldRoot: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      height: 50,
      "&:hover fieldset": {
        borderColor: "rgba(255,255,255, 0.25)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(255,255,255, 0.25)",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 10px",
    },
    borderRadius: "4px",
  },
  textFieldInput: {
    backgroundColor: "#ffffff",
    color: "#666666",
  },
  logo: {
    maxWidth: 180,
    width: "90%",
    maxHeight: 162,
    cursor: "pointer",
  },
  contentContainer: {
    maxWidth: 1280,
    margin: "auto",
    width: "100%",
    backgroundColor: "#001630",
    backgroundImage: "url(https://smalive.wisdom.com.au/app/uploads/2021/06/bg-overlay-03-1.jpg)",
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    minHeight: "100vh",
  },
  headerBody: {
    display: "flex",
    padding: 25,
  },
  logoWithArrow: {
    "@media (max-width: 600px)": {
      display: "flex",
      flexDirection: "column-reverse",
    },
  },
  widgetContent: {
    padding: 40,
    textAlign: "center",
  },
  headerLogin: {
    marginTop: 30,
    paddingBottom: 0,
  },
  heading: {
    fontSize: "100px",
    color: "#FFFFFF",
    fontWeight: 800,
    lineHeight: "1.2em",
    margin: 0,
  },
  loginText: {
    color: "#ffffff",
    marginBottom: 12,
    fontSize: 36,
    maxWidth: 540,

    "@media (max-width: 600px)": {
      fontSize: 20,
    },
  },
  formContainer: {
    display: "flex",

    "@media (max-width: 800px)": {
      display: "flex",
      flexDirection: "column",
    },
  },
  resetPassInputs: {
    backgroundColor: "#FFFFFF50",
    margin: "0px 5px 0px 5px",
    borderRadius: "4px",
    width: "43px",
    height: "50px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "32px",
    color: "#212121",
    position: "relative",
    cursor: "text",
  },
  resetDigits: {
    "@media (max-width: 600px)": {
      marginTop: "20px",
      marginLeft: "-5px",
    },
    "@media (max-width: 350px)": {
      marginLeft: "-15px",
    },
  },
}));

const LoginOTPForTablets: React.FC<LoginOTPForTabletsProps> = (props) => {
  const codeInput = React.useRef<ResetCodeComponent>(null);
  const history = useHistory();

  const classes = useStyles();

  const [errorMessage, setErrorMessage] = useState("");

  const goBack = () => {
    props.setLoginState(props.initialLoginState);
  };

  const LoginPage = () => {
    history.push("/login");
  };

  //call to check OTP
  const checkOTP = async (): Promise<void> => {
    let result = null;
    if (props.isSMS) {
      result = await props.dispatch(loginWithSMSOTP(props.username, props.password, codeInput.current?.state.value ?? ""));
    } else {
      result = await props.dispatch(loginWithOTP(props.username, props.password, codeInput.current?.state.value ?? ""));
    }
    if (result.result == 2) {
      props.finishLoginOTP(true, result.token ?? "");
    } else if (result.result == 1) {
      setErrorMessage("Sign-in is blocked\nYou've tried to sign in too many times with an incorrect account or password.");
    } else if (result.result == 4) {
      setErrorMessage("Your account is disabled at this time. For more information, please contact us on 1300 30 40 63");
    } else if (result.result == 9) {
      setErrorMessage(amountActiveSessionExceeded);
    } else {
      setErrorMessage("Invalid verification code");
    }
  };
  return (
    <div className={classes.contentContainer}>
      <Grid className={classes.headerBody} container>
        <Grid item xs={12} sm={4} className={classes.logoWithArrow}>
          <SMALogo className={classes.logo} onClick={LoginPage} />
          <div className={classes.backButtonContainer}>
            <Button className={classes.backButton} onClick={goBack}>
              <ArrowBackIcon />
            </Button>
          </div>
        </Grid>
        <Grid item xs={12} sm={8} className={classes.headerLogin}>
          <div className={classes.loginText}>
            {/[0-9]+/.test(props.username) ? "Enter the verification code we sent to your phone" : "Enter the verification code we just emailed you"}
          </div>

          <div className={classes.formContainer}>
            <span className={classes.resetDigits}>
              <ResetCodeComponent ref={codeInput} className={classes.resetPassInputs} />
            </span>

            <Button className={classes.button} variant="contained" onClick={checkOTP}>
              Continue
            </Button>
          </div>
          <Alert
            variant="filled"
            icon={<ReportProblemOutlinedIcon />}
            severity="error"
            classes={{ root: classes.alertErrorRoot }}
            style={{ display: errorMessage ? "flex" : "none" }}
          >
            {errorMessage}
          </Alert>
        </Grid>
      </Grid>
    </div>
  );
};

export default React.memo(LoginOTPForTablets);
